/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { BatchTestUtteranceRow } from 'src/luis/projects/testPane/components/BatchBlade/BatchTestInspector/BatchTestResultUtteranceTable/BatchTestUtteranceRow';
import { BatchTest } from 'src/luis/projects/testPane/types/BatchTest';
import { Utterance } from 'src/luis/projects/utterances/types/Utterance';
import { LuisId } from 'src/luis/types/Resource';

type BatchTestResultUtteranceTableProps = {
    /**
     * The batch test that was run.
     */
    batchTest: BatchTest;

    /**
     * The ids of the utterances that are selected now.
     */
    selectedUtteranceIds: LuisId[];

    /**
     * Determines if the utterance text mode is right to left or not
     */
    isRTL: boolean;
};

type BatchTestResultUtteranceTableItem = {
    utterance: Utterance;
    labeledIntent: string;
    predictedIntent: string;
};

const messages = defineMessages({
    exampleUtterance: {
        id: 'testPane.batchTestResultUtteranceTable.exampleUtterance',
        defaultMessage: 'Utterance'
    },
    labeledIntent: {
        id: 'testPane.batchTestResultUtteranceTable.labeledIntent',
        defaultMessage: 'Labeled intent'
    },
    predictedIntent: {
        id: 'testPane.batchTestResultUtteranceTable.predictedIntent',
        defaultMessage: 'Predicted intent'
    },
    emptyTablePlaceholder: {
        id: 'testPane.batchTestResultUtteranceTable.emptyTablePlaceholder',
        defaultMessage: 'No utterance(s) selected. Click on a data point or confusion type to see corresponding utterances'
    }
});

/**
 * Renders the utterance table that will display the utterances
 * that were selected in the batch test results graph.
 *
 * @todo Wait for utterance table implementation.
 */
export const BatchTestResultUtteranceTable = (props: BatchTestResultUtteranceTableProps) => {
    const { selectedUtteranceIds, batchTest, isRTL } = props;
    const { localize } = useLocalization();

    const items: BatchTestResultUtteranceTableItem[] = selectedUtteranceIds
        .map(id => batchTest.resultItems.find(rI => rI.id === id))
        .map(item => {
            const topScoringIntent = item.predictedIntents.sort((x, y) => (x.score < y.score ? 1 : -1))[0];
            const predictedIntentScore = `${topScoringIntent.intent.name} (${topScoringIntent.score})`;

            return {
                utterance: item,
                labeledIntent: item.labeledIntent.name,
                predictedIntent: predictedIntentScore
            };
        });

    /**
     * Utterance table columns.
     */
    const columns: IColumn[] = [
        {
            key: 'utterance',
            name: localize(messages.exampleUtterance),
            fieldName: 'utterance',
            minWidth: 100,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            isMultiline: true,
            onRender: (item: BatchTestResultUtteranceTableItem) => <BatchTestUtteranceRow utterance={item.utterance} isRTL={isRTL} />
        },
        {
            key: 'labeledIntent',
            name: localize(messages.labeledIntent),
            fieldName: 'labeledIntent',
            minWidth: 200,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: BatchTestResultUtteranceTableItem) => (
                <Stack verticalFill verticalAlign="center">
                    <LuisText>{item.labeledIntent}</LuisText>
                </Stack>
            )
        },
        {
            key: 'nearestIntent',
            name: localize(messages.predictedIntent),
            fieldName: 'predictedIntent',
            minWidth: 200,
            isResizable: true,
            isCollapsible: true,
            onRender: (item: BatchTestResultUtteranceTableItem) => (
                <Stack verticalFill verticalAlign="center">
                    <LuisText>{item.predictedIntent}</LuisText>
                </Stack>
            )
        }
    ];

    return (
        <Table<BatchTestResultUtteranceTableItem>
            items={items}
            getKey={(item: BatchTestResultUtteranceTableItem) => item.utterance.id.toString()}
            selectionMode={SelectionMode.none}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            columns={columns}
        />
    );
};
